import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { CardActions, createCardActionsProps } from "@/src/components/cards/card-actions";
import {
  EntityDropdownMenu,
  Field,
  FieldLayout,
  hasConflicts,
  LinkToEntity,
  renderBadge,
} from "@/src/components/cards/common";
import { PatientAddressField } from "@/src/components/cards/patient-card/patient-address-field";
import PatientCardEditable from "@/src/components/cards/patient-card/patient-card-editable";
import {
  PATIENT_HEADER_FIELD_KEYS,
  PatientCardTitle,
  getPatientCardDetails,
  preferMrn,
  withResolvedEhrIdFields,
} from "@/src/components/cards/patient-card/patient-card-header";
import ErrorAlert from "@/src/components/error-alert";
import { ContextMenu, ContextMenuTrigger } from "@/src/components/shadcn/ui/context-menu";
import { Skeleton } from "@/src/components/shadcn/ui/skeleton";
import { ValerieCard } from "@/src/components/valerie-card";
import { useUser } from "@/src/context/user-context";
import { logger } from "@/src/lib/logging/logger";
import { canAccessInbox, canWritePatient } from "@/src/lib/rbac";
import { displayEhrId, displayEhrIdentifiers } from "@/src/lib/resolve-ehr-ids";
import { getFormattedAddress } from "@/src/models/address";
import { Patient } from "@/src/models/patient";

export const PATIENT_FIELDS: Field[] = [
  { key: "familyName", label: "Family Name", comparable: true, required: true, section: "Demographics" },
  { key: "givenName", label: "Given Name", comparable: true, required: true, section: "Demographics" },
  { key: "middleName", label: "Middle Name", comparable: true, section: "Demographics" },
  { key: "dateOfBirth", label: "Date of Birth", type: "date", comparable: true, section: "Demographics" },
  { key: "gender", label: "Gender", comparable: true, section: "Demographics" },
  { key: "race", label: "Race", comparable: false, section: "Demographics" },
  { key: "ethnicity", label: "Ethnicity", comparable: false, section: "Demographics" },
  { key: "language", label: "Language", comparable: false, section: "Demographics" },
  { key: "type", label: "Type", comparable: false, section: "Demographics" },
  {
    key: "ehrId",
    label: "EHR ID",
    comparable: false,
    section: "Identifiers",
    getValue: (p: Patient) => displayEhrId(p),
  },
  { key: "homeAddress.line1", label: "Address", comparable: true, colSpan: 2, section: "Address" },
  { key: "homeAddress.line2", label: "Address 2", comparable: true, section: "Address" },
  { key: "homeAddress.city", label: "City", comparable: true, startsRow: true, section: "Address" },
  { key: "homeAddress.state", label: "State", comparable: true, section: "Address" },
  { key: "homeAddress.postalCode", label: "Zip Code", comparable: true, section: "Address" },
  { key: "homePhoneNumber", label: "Home Phone", type: "tel", comparable: true, section: "Contact" },
  { key: "mobilePhoneNumber", label: "Mobile Phone", type: "tel", comparable: true, section: "Contact" },
  { key: "personalEmail", label: "Email", comparable: true, section: "Contact" },
];

const ADDRESS_FIELDS = PATIENT_FIELDS.filter((field) => field.section === "Address");

const COMBINED_ADDRESS_FIELD: Field = {
  key: "homeAddress",
  label: "Address",
  comparable: true,
  section: "Address",
  getValue: (p: Patient) => getFormattedAddress(p.homeAddress),
};

function getCompareFields(fields: Field[], patient: Patient, secondaryPatient: Patient | null) {
  return fields.flatMap((field) => {
    if (field.section === "Address") {
      return field === ADDRESS_FIELDS[0] ? [COMBINED_ADDRESS_FIELD] : [];
    }

    if (
      PATIENT_HEADER_FIELD_KEYS.has(field.key) &&
      !(field.comparable && secondaryPatient && hasConflicts([field], patient, secondaryPatient))
    ) {
      return [];
    }

    return [field];
  });
}

export default function PatientCard({
  patient,
  compareMode = false,
  secondaryPatient: secondaryPatient = null,
  alwaysShowLink = false,
}: {
  patient: Patient;
  onClick?: () => void;
  compareMode?: boolean;
  secondaryPatient?: Patient | null;
  alwaysShowLink?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { group, roles } = useUser();
  const canWrite = canWritePatient(roles);
  const canContact = canAccessInbox(roles);

  const resolveIdentifiers = (p: Patient) => preferMrn(displayEhrIdentifiers(p, group));
  const fields = withResolvedEhrIdFields(PATIENT_FIELDS, resolveIdentifiers);

  useEffect(() => {
    logger.info("Patient in patient card", { patient, secondaryPatient });
  }, [patient, secondaryPatient]);

  let hasAnyConflicts = false;

  if (compareMode && secondaryPatient) {
    hasAnyConflicts = hasConflicts(fields, patient, secondaryPatient);
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (isEditing) {
    return (
      <PatientCardEditable
        patient={patient}
        onClose={handleEditToggle}
        compareMode={compareMode}
        secondaryObject={secondaryPatient}
        fields={PATIENT_FIELDS}
      />
    );
  }

  const cardActionsProps = createCardActionsProps({
    id: patient.id,
    label: "Patient ID",
    href: `/patients/${patient.displayId}`,
    customProps: [{ label: "Edit Patient", onClick: handleEditToggle }],
  });

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <ValerieCard
          className="w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          tabIndex={0}
          title={patient ? <PatientCardTitle patient={patient} /> : "Patient"}
          details={patient ? getPatientCardDetails(patient, resolveIdentifiers(patient)) : undefined}
          actions={
            <>
              {patient && renderBadge(compareMode, secondaryPatient, hasAnyConflicts)}
              {patient && (alwaysShowLink || !pathname?.startsWith(`/patients/${patient.displayId}`)) ? (
                <LinkToEntity href={`/patients/${patient.displayId}`} />
              ) : null}
              {canWrite && patient && (
                <EntityDropdownMenu
                  cardActionsProps={cardActionsProps}
                  customProps={
                    canContact
                      ? [{ label: "Contact Patient", onClick: () => router.push(`/inbox/${patient.displayId}`) }]
                      : []
                  }
                />
              )}
            </>
          }
        >
          {!patient ? (
            <ErrorAlert message="Patient not found" compact />
          ) : (
            <FieldLayout
              fields={
                compareMode
                  ? getCompareFields(fields, patient, secondaryPatient)
                  : fields.filter((field) => !PATIENT_HEADER_FIELD_KEYS.has(field.key))
              }
              patient={patient}
              secondaryObject={secondaryPatient}
              compareMode={compareMode}
              renderField={(field) =>
                compareMode && field === COMBINED_ADDRESS_FIELD ? (
                  <PatientAddressField
                    address={patient.homeAddress}
                    secondaryAddress={secondaryPatient?.homeAddress ?? null}
                    hasConflict={!!secondaryPatient && hasConflicts(ADDRESS_FIELDS, patient, secondaryPatient)}
                  />
                ) : undefined
              }
            />
          )}
        </ValerieCard>
      </ContextMenuTrigger>
      {canWrite && patient && <CardActions {...cardActionsProps} isContextMenu={true} />}
    </ContextMenu>
  );
}

function GridItemSkeleton() {
  return <Skeleton className="inline-block align-text-bottom h-4 w-[100px]" />;
}

export function PatientCardSkeleton() {
  return (
    <ValerieCard
      className="w-full"
      title={<Skeleton className="h-6 w-48" />}
      description={<Skeleton className="mt-1 h-4 w-56" />}
    >
      <div className="grid grid-cols-3 gap-4">
        {Array.from({ length: 6 }, (_, index) => (
          <GridItemSkeleton key={index} />
        ))}
      </div>
    </ValerieCard>
  );
}
